import * as React from 'react';

import { Box } from '../box';
import { styled } from '../styled';
import { usePaletteColor } from '../theme';
import { IconProps } from './base-icon';
import { materialSymbolUnicode } from './material-symbols-unicode';

declare global {
    // eslint-disable-next-line no-unused-vars
    interface Window {
        __materialSymbolsStylesheetReady?: Promise<void>;
    }
}

type MaterialSymbolName =
    | keyof typeof materialSymbolUnicode
    | 'draft'
    | 'location_on'
    | 'smartphone';

export interface MaterialSymbolProps extends IconProps {
    name: MaterialSymbolName;
}

const materialSymbolFallbacks: Record<
    Exclude<MaterialSymbolName, keyof typeof materialSymbolUnicode>,
    keyof typeof materialSymbolUnicode
> = {
    draft: 'drafts',
    location_on: 'place',
    smartphone: 'mobile'
};

export function loadMaterialSymbols(): Promise<void> {
    const stylesheetReady = (async () => {
        // eslint-disable-next-line import/no-internal-modules
        await import('./fonts/material-symbols-rounded-subset.css');
    })();

    if (typeof window !== 'undefined') {
        window.__materialSymbolsStylesheetReady = stylesheetReady;
    }

    return stylesheetReady;
}

const fontSizes = {
    inherit: 'inherit',
    large: '35px',
    medium: '24px',
    small: '20px'
} as const;

const mapFontSize = (fontSize: string | number | undefined) => {
    return typeof fontSize === 'string' && fontSize in fontSizes
        ? fontSizes[fontSize as keyof typeof fontSizes]
        : fontSize;
};

const MaterialSymbolBox = styled(Box)<{
    $color: string | undefined;
    $filled: boolean;
    $fontSize: string | number | undefined;
    $symbol: string;
}>(({ $color, $filled, $fontSize, $symbol }) => ({
    color: $color,
    display: 'inline-flex',
    flexShrink: 0,
    fontFamily: 'Material Symbols Rounded Variable',
    fontSize: $fontSize,
    fontVariationSettings: `'FILL' ${$filled ? 1 : 0}, 'wght' 400, 'GRAD' 0, 'opsz' 24`,
    lineHeight: 1,
    ':after': {
        content: `"${$symbol}"`
    }
}));

export const MaterialSymbol = React.forwardRef<
    SVGSVGElement,
    MaterialSymbolProps
>((props, ref) => {
    const {
        color: colorProp = 'action.active',
        filled = false,
        fontSize: _fontSize = 'medium',
        name: _name,
        className,
        onClick,
        titleAccess,
        ...htmlProps
    } = props;
    const color = usePaletteColor(colorProp);
    const name =
        _name in materialSymbolFallbacks
            ? materialSymbolFallbacks[
                  _name as keyof typeof materialSymbolFallbacks
              ]
            : _name;
    const fontSize = mapFontSize(_fontSize);

    const codepoint =
        materialSymbolUnicode[name as keyof typeof materialSymbolUnicode];

    if (codepoint === undefined) {
        throw new Error(`[MaterialSymbol] Unsupported symbol: ${_name}`);
    }

    const symbol = `\\${codepoint.toString(16).toUpperCase()} `;

    return (
        <MaterialSymbolBox
            ref={ref as unknown as React.Ref<HTMLSpanElement>}
            component="span"
            data-role="icon"
            data-role-value={name}
            $color={color}
            $filled={filled}
            $fontSize={fontSize}
            $symbol={symbol}
            aria-hidden={titleAccess ? undefined : true}
            className={className}
            onClick={
                onClick as unknown as React.MouseEventHandler<HTMLDivElement>
            }
            title={titleAccess}
            {...(htmlProps as React.HTMLAttributes<HTMLSpanElement>)}
        />
    );
});

const createMaterialSymbol = (name: MaterialSymbolProps['name']) =>
    React.forwardRef<SVGSVGElement, IconProps>((props, ref) => (
        <MaterialSymbol ref={ref} {...props} name={name} />
    ));

export const IconAccountBalance = createMaterialSymbol('account_balance');
export const IconAccessTime = createMaterialSymbol('schedule');
export const IconAdd = createMaterialSymbol('add');
export const IconAddCard = createMaterialSymbol('add_card');
export const IconAddCircleOutline = createMaterialSymbol('add_circle');
export const IconArrowBack = createMaterialSymbol('arrow_back');
export const IconAccountCircle = createMaterialSymbol('account_circle');
export const IconAddPhotoAlternate = createMaterialSymbol('photo_camera');
export const IconAlternateEmail = createMaterialSymbol('alternate_email');
export const IconBarChart = createMaterialSymbol('bar_chart');
export const IconAnalytics = createMaterialSymbol('bar_chart');
export const IconApps = createMaterialSymbol('apps');
export const IconArchive = createMaterialSymbol('archive');
export const IconArrowDownward = createMaterialSymbol('arrow_downward');
export const IconArrowDropDown = createMaterialSymbol('arrow_drop_down');
export const IconArrowDropUp = createMaterialSymbol('arrow_drop_up');
export const IconArrowForward = createMaterialSymbol('arrow_forward');
export const IconArrowUpward = createMaterialSymbol('arrow_upward');
export const IconArticle = createMaterialSymbol('article');
export const IconAttachFile = createMaterialSymbol('attach_file');
export const IconAutoAwesome = createMaterialSymbol('wand_stars');
export const IconBackspace = createMaterialSymbol('backspace');
export const IconBlock = createMaterialSymbol('block');
export const IconBolt = createMaterialSymbol('bolt');
export const IconCake = createMaterialSymbol('cake');
export const IconCalendarClock = createMaterialSymbol('calendar_clock');
export const IconCalendarMonth = createMaterialSymbol('calendar_month');
export const IconCalendarToday = createMaterialSymbol('calendar_today');
export const IconCalendarViewMonth = createMaterialSymbol(
    'calendar_view_month'
);
export const IconCameraAlt = createMaterialSymbol('photo_camera');
export const IconCardGiftcard = createMaterialSymbol('redeem');
export const IconCardMembership = createMaterialSymbol('card_membership');
export const IconCheck = createMaterialSymbol('check');
export const IconCheckBoxOutlineBlank = createMaterialSymbol(
    'check_box_outline_blank'
);
export const IconCheckCircle = createMaterialSymbol('check_circle');
export const IconChevronLeft = createMaterialSymbol('chevron_left');
export const IconChevronRight = createMaterialSymbol('chevron_right');
export const IconCircle = createMaterialSymbol('circle');
export const IconClose = createMaterialSymbol('close');
export const IconContentCopy = createMaterialSymbol('content_copy');
export const IconDateRange = createMaterialSymbol('date_range');
export const IconDelete = createMaterialSymbol('delete');
export const IconDescription = createMaterialSymbol('description');
export const IconDeviceHub = createMaterialSymbol('person_add');
export const IconDevices = createMaterialSymbol('devices');
export const IconDistance = createMaterialSymbol('distance');
export const IconDoneAll = createMaterialSymbol('done_all');
export const IconDragIndicator = createMaterialSymbol('drag_indicator');
export const IconEdit = createMaterialSymbol('edit');
export const IconError = createMaterialSymbol('error');
export const IconEventAvailable = createMaterialSymbol('event_available');
export const IconEventBusy = createMaterialSymbol('event_busy');
export const IconDashboard = createMaterialSymbol('dashboard');
export const IconEventRepeat = createMaterialSymbol('event_repeat');
export const IconExercise = createMaterialSymbol('exercise');
export const IconExpand = createMaterialSymbol('expand');
export const IconExpandLess = createMaterialSymbol('keyboard_arrow_up');
export const IconExpandMore = createMaterialSymbol('keyboard_arrow_down');
export const IconFavorite = createMaterialSymbol('favorite');
export const IconFileDownload = createMaterialSymbol('download');
export const IconFilterList = createMaterialSymbol('filter_list');
export const IconFolderOpen = createMaterialSymbol('folder_open');
export const IconFormatAlignCenter = createMaterialSymbol(
    'format_align_center'
);
export const IconFormatAlignJustify = createMaterialSymbol(
    'format_align_justify'
);
export const IconFormatAlignLeft = createMaterialSymbol('format_align_left');
export const IconFormatAlignRight = createMaterialSymbol('format_align_right');
export const IconFormatBold = createMaterialSymbol('format_bold');
export const IconFormatClear = createMaterialSymbol('format_clear');
export const IconFormatColorFill = createMaterialSymbol('format_color_fill');
export const IconFormatColorText = createMaterialSymbol('format_color_text');
export const IconFormatIndentDecrease = createMaterialSymbol(
    'format_indent_decrease'
);
export const IconFormatIndentIncrease = createMaterialSymbol(
    'format_indent_increase'
);
export const IconFormatItalic = createMaterialSymbol('format_italic');
export const IconFormatListBulleted = createMaterialSymbol(
    'format_list_bulleted'
);
export const IconFormatListNumbered = createMaterialSymbol(
    'format_list_numbered'
);
export const IconFormatUnderlined = createMaterialSymbol('format_underlined');
export const IconGridOn = createMaterialSymbol('grid_on');
export const IconGroup = createMaterialSymbol('group');
export const IconForum = createMaterialSymbol('forum');
export const IconGroupAdd = createMaterialSymbol('group_add');
export const IconHome = createMaterialSymbol('home');
export const IconHowToReg = createMaterialSymbol('how_to_reg');
export const IconHelp = createMaterialSymbol('help');
export const IconImage = createMaterialSymbol('image');
export const IconInfo = createMaterialSymbol('info');
export const IconInsertChart = createMaterialSymbol('insert_chart');
export const IconInsertDriveFile = createMaterialSymbol('draft');
export const IconKeyboardArrowDown = createMaterialSymbol(
    'keyboard_arrow_down'
);
export const IconKeyboardArrowRight = createMaterialSymbol(
    'keyboard_arrow_right'
);
export const IconKeyboardArrowUp = createMaterialSymbol('keyboard_arrow_up');
export const IconLanguage = createMaterialSymbol('language');
export const IconLeftPanelClose = createMaterialSymbol('left_panel_close');
export const IconLink = createMaterialSymbol('link');
export const IconLinkOff = createMaterialSymbol('link_off');
export const IconLocationOn = createMaterialSymbol('location_on');
export const IconLock = createMaterialSymbol('lock');
export const IconLogout = createMaterialSymbol('logout');
export const IconMail = createMaterialSymbol('mail');
export const IconMarkEmailRead = createMaterialSymbol('mark_email_read');
export const IconMenu = createMaterialSymbol('menu');
export const IconFemale = createMaterialSymbol('female');
export const IconMale = createMaterialSymbol('male');
export const IconTransgender = createMaterialSymbol('transgender');
export const IconMonetizationOn = createMaterialSymbol('paid');
export const IconMoreHoriz = createMaterialSymbol('more_horiz');
export const IconMoreVert = createMaterialSymbol('more_vert');
export const IconNotifications = createMaterialSymbol('notifications');
export const IconOpenInNew = createMaterialSymbol('open_in_new');
export const IconPaid = createMaterialSymbol('paid');
export const IconPictureAsPdf = createMaterialSymbol('picture_as_pdf');
export const IconPlayArrow = createMaterialSymbol('play_arrow');
export const IconRedo = createMaterialSymbol('redo');
export const IconRemove = createMaterialSymbol('remove');
export const IconDoNotDisturbOn = createMaterialSymbol('do_not_disturb_on');
export const IconReplay = createMaterialSymbol('sync');
export const IconSearch = createMaterialSymbol('search');
export const IconSell = createMaterialSymbol('sell');
export const IconSave = createMaterialSymbol('save');
export const IconSchedule = createMaterialSymbol('schedule');
export const IconSettings = createMaterialSymbol('settings');
export const IconShoppingCart = createMaterialSymbol('shopping_cart');
export const IconSkipNext = createMaterialSymbol('skip_next');
export const IconSmartphone = createMaterialSymbol('smartphone');
export const IconSmartToy = createMaterialSymbol('smart_toy');
export const IconScience = createMaterialSymbol('science');
export const IconSend = createMaterialSymbol('send');
export const IconStop = createMaterialSymbol('stop');
export const IconStars2 = createMaterialSymbol('stars_2');
export const IconStorefront = createMaterialSymbol('storefront');
export const IconSupportAgent = createMaterialSymbol('support_agent');
export const IconSwapHoriz = createMaterialSymbol('swap_horiz');
export const IconSwapVert = createMaterialSymbol('swap_vert');
export const IconSwitchAccount = createMaterialSymbol('switch_account');
export const IconSync = createMaterialSymbol('sync');
export const IconTableChart = createMaterialSymbol('table_chart');
export const IconTableRows = createMaterialSymbol('table_rows');
export const IconTask = createMaterialSymbol('task');
export const IconTaskAlt = createMaterialSymbol('task_alt');
export const IconTextFields = createMaterialSymbol('text_fields');
export const IconTextSnippet = createMaterialSymbol('text_snippet');
export const IconTextsms = createMaterialSymbol('sms');
export const IconThumbDown = createMaterialSymbol('thumb_down');
export const IconThumbUp = createMaterialSymbol('thumb_up');
export const IconTimer = createMaterialSymbol('timer');
export const IconToday = createMaterialSymbol('today');
export const IconTune = createMaterialSymbol('tune');
export const IconUndo = createMaterialSymbol('undo');
export const IconUploadFile = createMaterialSymbol('upload_file');
export const IconVerticalAlignBottom = createMaterialSymbol(
    'vertical_align_bottom'
);
export const IconVerticalAlignCenter = createMaterialSymbol(
    'vertical_align_center'
);
export const IconVerticalAlignTop = createMaterialSymbol('vertical_align_top');
export const IconVideocamOff = createMaterialSymbol('videocam_off');
export const IconViewColumn = createMaterialSymbol('view_column');
export const IconVisibility = createMaterialSymbol('visibility');
export const IconVisibilityOff = createMaterialSymbol('visibility_off');
export const IconWarning = createMaterialSymbol('warning');
export const IconWindow = createMaterialSymbol('window');
export const IconWrapText = createMaterialSymbol('wrap_text');
export const IconZoomOutMap = createMaterialSymbol('zoom_out_map');
export const IconTopic = createMaterialSymbol('topic');
export const IconWidgets = createMaterialSymbol('widgets');
export const IconQuestionAnswer = createMaterialSymbol('forum');

export const materialSymbolIcons = {
    IconAccessTime,
    IconAccountBalance,
    IconAccountCircle,
    IconAdd,
    IconAddCard,
    IconAddCircleOutline,
    IconArrowBack,
    IconAddPhotoAlternate,
    IconAlternateEmail,
    IconAnalytics,
    IconApps,
    IconArchive,
    IconArrowDownward,
    IconArrowDropDown,
    IconArrowDropUp,
    IconArrowForward,
    IconArrowUpward,
    IconArticle,
    IconAttachFile,
    IconAutoAwesome,
    IconBackspace,
    IconBarChart,
    IconBlock,
    IconBolt,
    IconCake,
    IconCalendarClock,
    IconCalendarMonth,
    IconCalendarToday,
    IconCalendarViewMonth,
    IconCameraAlt,
    IconCardGiftcard,
    IconCardMembership,
    IconCheck,
    IconCheckBoxOutlineBlank,
    IconCheckCircle,
    IconChevronLeft,
    IconChevronRight,
    IconCircle,
    IconClose,
    IconContentCopy,
    IconDashboard,
    IconDateRange,
    IconDelete,
    IconDescription,
    IconDoNotDisturbOn,
    IconDeviceHub,
    IconDevices,
    IconDistance,
    IconDoneAll,
    IconDragIndicator,
    IconEdit,
    IconError,
    IconEventAvailable,
    IconEventBusy,
    IconEventRepeat,
    IconExercise,
    IconExpand,
    IconExpandLess,
    IconExpandMore,
    IconFavorite,
    IconFileDownload,
    IconFemale,
    IconFilterList,
    IconFolderOpen,
    IconFormatAlignCenter,
    IconFormatAlignJustify,
    IconFormatAlignLeft,
    IconFormatAlignRight,
    IconFormatBold,
    IconFormatClear,
    IconFormatColorFill,
    IconFormatColorText,
    IconFormatIndentDecrease,
    IconFormatIndentIncrease,
    IconFormatItalic,
    IconFormatListBulleted,
    IconFormatListNumbered,
    IconForum,
    IconFormatUnderlined,
    IconGridOn,
    IconGroup,
    IconGroupAdd,
    IconHelp,
    IconHome,
    IconHowToReg,
    IconImage,
    IconInfo,
    IconInsertChart,
    IconInsertDriveFile,
    IconKeyboardArrowDown,
    IconKeyboardArrowRight,
    IconKeyboardArrowUp,
    IconLanguage,
    IconLeftPanelClose,
    IconLink,
    IconLinkOff,
    IconLocationOn,
    IconLock,
    IconLogout,
    IconMail,
    IconMale,
    IconMarkEmailRead,
    IconMenu,
    IconMonetizationOn,
    IconMoreHoriz,
    IconMoreVert,
    IconNotifications,
    IconOpenInNew,
    IconPaid,
    IconPictureAsPdf,
    IconPlayArrow,
    IconRedo,
    IconRemove,
    IconReplay,
    IconSave,
    IconSchedule,
    IconScience,
    IconSearch,
    IconSell,
    IconSend,
    IconSettings,
    IconShoppingCart,
    IconSkipNext,
    IconSmartphone,
    IconSmartToy,
    IconStars2,
    IconStop,
    IconStorefront,
    IconSupportAgent,
    IconSwapHoriz,
    IconSwapVert,
    IconSwitchAccount,
    IconSync,
    IconTableChart,
    IconTableRows,
    IconTask,
    IconTaskAlt,
    IconTransgender,
    IconTextFields,
    IconTextSnippet,
    IconTextsms,
    IconThumbDown,
    IconThumbUp,
    IconTimer,
    IconToday,
    IconTopic,
    IconTune,
    IconUndo,
    IconUploadFile,
    IconVerticalAlignBottom,
    IconVerticalAlignCenter,
    IconVerticalAlignTop,
    IconVideocamOff,
    IconViewColumn,
    IconVisibility,
    IconVisibilityOff,
    IconWarning,
    IconWindow,
    IconWrapText,
    IconZoomOutMap,
    IconQuestionAnswer,
    IconWidgets
};
